import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Search, Rocket, RefreshCw, AlertTriangle, Compass, Zap, Clock, Grid3x3,
  ChevronRight, TrendingUp, Wallet, Layers,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const shortAddr = (a) => (a && a.length > 8 ? `${a.slice(0, 4)}…${a.slice(-4)}` : a || "");

/* -------------------------------------------------------------
 * Token Card — reusable, backend-driven
 * ----------------------------------------------------------- */
export function TokenCard({ token }) {
  const navigate = useNavigate();
  const created = token.createdAt ? new Date(token.createdAt) : null;
  const network = (token.network || "").toUpperCase();
  const netClass = network === "LOCALNET" ? "is-local"
                 : network === "DEVNET"   ? "is-dev"
                 : "is-main";
  const netLabel = network === "LOCALNET" ? "LOCALNET TEST"
                 : network === "DEVNET"   ? "DEVNET TEST"
                 : network || "TEST";
  const isImage = typeof token.image === "string" && token.image.startsWith("data:");

  const onOpen = () => navigate(`/launchpad/token/${token.mintAddress}`);
  const onKeyOpen = (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(); } };

  return (
    <article
      className="sticky-lp-tokencard"
      role="link"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={onKeyOpen}
      data-testid={`lp-explore-card-${token.mintAddress}`}
    >
      <header className="sticky-lp-tokencard-head">
        <span className="sticky-lp-tokencard-logo" aria-hidden="true">
          {isImage ? <img src={token.image} alt="" /> : <Rocket size={18} strokeWidth={2.4} />}
        </span>
        <div className="sticky-lp-tokencard-title">
          <div className="sticky-lp-tokencard-name">{token.name || "Untitled Token"}</div>
          <div className="sticky-lp-tokencard-sym">${(token.symbol || "").toUpperCase() || "???"}</div>
        </div>
        <span className={`sticky-lp-tokencard-net ${netClass}`}>
          <span className="sticky-lp-netdot" />{netLabel}
        </span>
      </header>

      {token.description && (
        <p className="sticky-lp-tokencard-desc">{token.description}</p>
      )}

      <dl className="sticky-lp-tokencard-meta">
        <div>
          <dt><Wallet size={10} /> Creator</dt>
          <dd><code>{shortAddr(token.creatorWallet)}</code></dd>
        </div>
        <div>
          <dt><Layers size={10} /> Mint</dt>
          <dd><code>{shortAddr(token.mintAddress)}</code></dd>
        </div>
        <div>
          <dt><Clock size={10} /> Created</dt>
          <dd>{created ? created.toLocaleDateString() : "—"}</dd>
        </div>
      </dl>

      <button
        type="button"
        className="sticky-lp-btn is-primary sticky-lp-tokencard-cta"
        onClick={(e) => { e.stopPropagation(); onOpen(); }}
        data-testid={`lp-explore-view-${token.mintAddress}`}
      >
        View Token <ChevronRight size={12} />
      </button>
    </article>
  );
}

/* -------------------------------------------------------------
 * Skeleton loader
 * ----------------------------------------------------------- */
function TokenCardSkeleton() {
  return (
    <div className="sticky-lp-tokencard is-skeleton" aria-hidden="true">
      <div className="sticky-lp-tokencard-head">
        <span className="sticky-lp-tokencard-logo sk-block" />
        <div className="sticky-lp-tokencard-title">
          <div className="sk-line sk-line-w60" />
          <div className="sk-line sk-line-w30" />
        </div>
        <span className="sk-line sk-line-w20" />
      </div>
      <div className="sk-line sk-line-full" />
      <div className="sk-line sk-line-w80" />
      <div className="sk-line sk-line-w50" />
    </div>
  );
}

/* -------------------------------------------------------------
 * Explore Section — hero, stats, search, filters, grid
 * ----------------------------------------------------------- */
export function ExploreSection() {
  const [tokens, setTokens] = useState([]);
  const [status, setStatus] = useState("loading"); // loading | ok | error
  const [err, setErr] = useState("");
  const [query, setQuery] = useState("");
  const [sortMode, setSortMode] = useState("latest"); // all | latest | trending
  const [netFilter, setNetFilter] = useState("ALL");  // ALL | LOCALNET | DEVNET

  const load = useCallback(async () => {
    setStatus("loading");
    setErr("");
    try {
      const res = await fetch(`${BACKEND_URL}/api/launchpad/tokens?limit=50`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setTokens(Array.isArray(data?.tokens) ? data.tokens : []);
      setStatus("ok");
    } catch (e) {
      setErr(String(e?.message || e || "Request failed"));
      setStatus("error");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const stats = useMemo(() => {
    const localnet = tokens.filter((t) => (t.network || "").toUpperCase() === "LOCALNET").length;
    const devnet   = tokens.filter((t) => (t.network || "").toUpperCase() === "DEVNET").length;
    return { total: tokens.length, localnet, devnet };
  }, [tokens]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = tokens.slice();
    // network filter
    if (netFilter !== "ALL") {
      list = list.filter((t) => (t.network || "").toUpperCase() === netFilter);
    }
    // text search across name, symbol, mint, creator
    if (q) {
      list = list.filter((t) => {
        const hay = [t.name, t.symbol, t.mintAddress, t.creatorWallet]
          .filter(Boolean).map(String).map((s) => s.toLowerCase());
        return hay.some((s) => s.includes(q));
      });
    }
    // sort mode
    if (sortMode === "latest") {
      list.sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || ""));
    } else if (sortMode === "trending") {
      // Simple activity ranking: newest first, weighted lightly by
      // description length (proxy for "real" launches over blanks).
      // NOT market data — clearly labeled as "Early Launchpad Activity".
      list.sort((a, b) => {
        const ta = new Date(a.createdAt || 0).getTime();
        const tb = new Date(b.createdAt || 0).getTime();
        const wa = (a.description || "").length > 0 ? 1 : 0;
        const wb = (b.description || "").length > 0 ? 1 : 0;
        return (tb + wb * 1e6) - (ta + wa * 1e6);
      });
    } // "all" = no reorder (insertion order from API which is already sorted desc)
    return list;
  }, [tokens, query, sortMode, netFilter]);

  return (
    <>
      <section className="sticky-lp-hero sticky-lp-hero-sm" data-testid="lp-explore-hero">
        <span className="sticky-lp-launched-badge is-inline">
          <Rocket size={11} /> STICKY LAUNCHPAD
        </span>
        <h1 className="sticky-lp-h1">Explore <span className="sticky-accent">Tokens</span></h1>
        <p className="sticky-lp-lede">
          Discover tokens launched through the Sticky Launchpad.
        </p>
      </section>

      {/* Stats — backend-only, no fake market data */}
      <section className="sticky-lp-card sticky-lp-explore-stats" data-testid="lp-explore-stats">
        <div>
          <span>Tokens Launched</span>
          <b data-testid="lp-stat-total">{stats.total}</b>
        </div>
        <div>
          <span>Localnet Tests</span>
          <b data-testid="lp-stat-localnet">{stats.localnet}</b>
        </div>
        <div>
          <span>Devnet Tests</span>
          <b data-testid="lp-stat-devnet">{stats.devnet}</b>
        </div>
        <Link to="/launchpad/create" className="sticky-lp-btn is-primary sticky-lp-explore-launch" data-testid="lp-explore-launch">
          <Rocket size={13} /> Launch Your Token
        </Link>
      </section>

      {/* Search + filters */}
      <section className="sticky-lp-card sticky-lp-explore-controls" data-testid="lp-explore-controls">
        <label className="sticky-lp-explore-search">
          <Search size={14} strokeWidth={2.4} />
          <input
            type="search"
            placeholder="Search by name, symbol, mint or creator…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            spellCheck={false}
            autoComplete="off"
            data-testid="lp-explore-search"
          />
          {query && (
            <button
              type="button"
              className="sticky-lp-explore-clear"
              onClick={() => setQuery("")}
              aria-label="Clear search"
              data-testid="lp-explore-search-clear"
            >×</button>
          )}
        </label>

        <div className="sticky-lp-explore-filters" role="tablist" aria-label="Sort tokens">
          <button type="button" role="tab" aria-selected={sortMode === "all"}
            className={`sticky-lp-filter ${sortMode === "all" ? "is-active" : ""}`}
            onClick={() => setSortMode("all")}
            data-testid="lp-filter-all"
          ><Grid3x3 size={11} /> All</button>
          <button type="button" role="tab" aria-selected={sortMode === "latest"}
            className={`sticky-lp-filter ${sortMode === "latest" ? "is-active" : ""}`}
            onClick={() => setSortMode("latest")}
            data-testid="lp-filter-latest"
          ><Clock size={11} /> Latest</button>
          <button type="button" role="tab" aria-selected={sortMode === "trending"}
            className={`sticky-lp-filter ${sortMode === "trending" ? "is-active" : ""}`}
            onClick={() => setSortMode("trending")}
            data-testid="lp-filter-trending"
          ><TrendingUp size={11} /> Trending</button>
        </div>

        <div className="sticky-lp-explore-filters is-sub" role="tablist" aria-label="Network filter">
          <button type="button" role="tab" aria-selected={netFilter === "ALL"}
            className={`sticky-lp-filter ${netFilter === "ALL" ? "is-active" : ""}`}
            onClick={() => setNetFilter("ALL")}
            data-testid="lp-netfilter-all"
          >All Networks</button>
          <button type="button" role="tab" aria-selected={netFilter === "LOCALNET"}
            className={`sticky-lp-filter is-local ${netFilter === "LOCALNET" ? "is-active" : ""}`}
            onClick={() => setNetFilter("LOCALNET")}
            data-testid="lp-netfilter-localnet"
          ><span className="sticky-lp-netdot" /> Localnet</button>
          <button type="button" role="tab" aria-selected={netFilter === "DEVNET"}
            className={`sticky-lp-filter is-dev ${netFilter === "DEVNET" ? "is-active" : ""}`}
            onClick={() => setNetFilter("DEVNET")}
            data-testid="lp-netfilter-devnet"
          ><span className="sticky-lp-netdot" /> Devnet</button>
          <button type="button" role="tab" aria-selected={false}
            className="sticky-lp-filter is-main is-disabled"
            disabled
            title="Mainnet Launchpad is not live yet"
            data-testid="lp-netfilter-mainnet"
          ><span className="sticky-lp-netdot" /> Mainnet 🔒</button>
        </div>
      </section>

      {/* Section header for the current mode */}
      <section className="sticky-lp-card">
        <div className="sticky-lp-card-head">
          <h2 className="sticky-lp-h2">
            {sortMode === "trending"
              ? <>Trending <span className="sticky-accent">— Early Launchpad Activity</span></>
              : sortMode === "latest"
                ? <>Latest <span className="sticky-accent">Launches</span></>
                : <>All <span className="sticky-accent">Tokens</span></>}
          </h2>
          <span className="sticky-lp-help-inline" data-testid="lp-explore-count">
            {status === "ok" ? `${filtered.length} of ${tokens.length}` : "—"}
          </span>
        </div>

        {sortMode === "trending" && status === "ok" && (
          <p className="sticky-lp-note" data-testid="lp-trending-note">
            Trending here reflects recent Launchpad activity only. Price, volume, market cap and holders will appear when real market data is live.
          </p>
        )}

        {/* Loading */}
        {status === "loading" && (
          <div className="sticky-lp-explore-grid" data-testid="lp-explore-loading">
            <TokenCardSkeleton /><TokenCardSkeleton /><TokenCardSkeleton />
          </div>
        )}

        {/* Error */}
        {status === "error" && (
          <div className="sticky-lp-explore-empty" data-testid="lp-explore-error">
            <AlertTriangle size={22} />
            <h3>Unable to load Launchpad tokens.</h3>
            <p>{err}</p>
            <button
              type="button"
              className="sticky-lp-btn is-primary"
              onClick={load}
              data-testid="lp-explore-retry"
            >
              <RefreshCw size={13} /> Retry
            </button>
          </div>
        )}

        {/* Empty — no tokens at all */}
        {status === "ok" && tokens.length === 0 && (
          <div className="sticky-lp-explore-empty" data-testid="lp-explore-empty">
            <Rocket size={22} />
            <h3>No tokens launched yet.</h3>
            <p>Be the first to launch a token on Sticky Launchpad.</p>
            <Link to="/launchpad/create" className="sticky-lp-btn is-primary" data-testid="lp-explore-empty-create">
              <Rocket size={13} /> Create Token
            </Link>
          </div>
        )}

        {/* No results for the current query but tokens exist */}
        {status === "ok" && tokens.length > 0 && filtered.length === 0 && (
          <div className="sticky-lp-explore-empty" data-testid="lp-explore-noresults">
            <Search size={22} />
            <h3>No tokens match your search.</h3>
            <p>Try a different name, symbol, mint or creator wallet.</p>
            <button type="button" className="sticky-lp-btn is-ghost" onClick={() => { setQuery(""); setNetFilter("ALL"); }}>
              <Compass size={13} /> Clear filters
            </button>
          </div>
        )}

        {/* Grid */}
        {status === "ok" && filtered.length > 0 && (
          <div className="sticky-lp-explore-grid" data-testid="lp-explore-grid">
            {filtered.map((t) => <TokenCard key={t.mintAddress} token={t} />)}
          </div>
        )}
      </section>
    </>
  );
}
